import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import classNames from "classnames";
import { Tooltip } from "@toddle-edu/ds-web";

import scrollBarWidth from "Utils/scrollBarWidth";
import classes from "./LeftPane.scss";

// offset applied so that scrollbar does not overlap content
const OFFSET = 4;

const LeftPane = ({
  data,
  activeItem,
  onItemClick,
  itemKey,
  labelKey,
  outlineLabel,
}) => {
  const [visibleTooltips, setVisibleTooltips] = useState({});

  const elementsRef = useRef({});
  const scrollRef = useRef(null);

  const checkOverflow = elementId => {
    const element = elementsRef.current[elementId];
    if (element) {
      // applied 8px extra as truncation occur keeping padding in mind
      const doesOverflow = element.scrollWidth + 8 > element.offsetWidth;
      setVisibleTooltips(prev => ({
        ...prev,
        [elementId]: doesOverflow,
      }));
    }
  };

  const checkScrollOverflow = () => {
    if (scrollRef.current) {
      const { scrollHeight, clientHeight } = scrollRef.current;
      const isScrollOverflow = scrollHeight > clientHeight;

      if (isScrollOverflow) {
        scrollRef.current.style.paddingRight = `${OFFSET}px`;
      } else {
        const paddingRight = scrollBarWidth + OFFSET;
        scrollRef.current.style.paddingRight = `${paddingRight}px`;
      }
    }
  };

  useEffect(() => {
    // Check overflow for all labels
    const ids = [];

    _.forEach(data, item => {
      const itemId = _.get(item, itemKey);
      ids.push(itemId);
    });

    ids.forEach(checkOverflow);

    // Check scroll overflow
    checkScrollOverflow();

    const handleResize = () => {
      ids.forEach(checkOverflow);
      checkScrollOverflow();
    };

    window.addEventListener("resize", handleResize);

    // Set up ResizeObserver to detect size changes in scroll container
    const resizeObserver = new ResizeObserver(() => {
      checkScrollOverflow();
    });

    if (scrollRef.current) {
      resizeObserver.observe(scrollRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [data, itemKey]);

  return (
    <>
      {outlineLabel && (
        <div className={classes.outlineTextContainer}>
          <div className={classNames("text-body-3", classes.outlineText)}>
            {outlineLabel}
          </div>
        </div>
      )}
      <div className={classes.leftPaneContainer} ref={scrollRef}>
        {_.map(data, item => {
          const itemTitle = _.get(item, labelKey, "Untitled Item");
          const itemId = _.get(item, itemKey);

          return (
            <div key={_.get(item, itemKey)} className={classes.stage}>
              {!_.isEmpty(_.get(item, labelKey)) && (
                <div className={classes.stageTitleContainer}>
                  <Tooltip
                    tooltip={itemTitle}
                    placement="top"
                    theme="dark"
                    dsVersion="2.0"
                    showArrow={true}
                    visible={visibleTooltips[itemId]}
                  >
                    <button
                      ref={el => (elementsRef.current[itemId] = el)}
                      type="button"
                      className={classNames(
                        "text-label-3",
                        classes.stageTitle,
                        "truncate",
                        {
                          [classes.active]: _.get(item, itemKey) === activeItem,
                        }
                      )}
                      onClick={() => onItemClick(_.get(item, itemKey))}
                    >
                      {itemTitle}
                    </button>
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

LeftPane.defaultProps = {
  itemKey: "id",
  labelKey: "label",
  outlineLabel: "OUTLINE",
  customClasses: {},
};

export default LeftPane;
